import {
  Alert,
  Button,
  Divider,
  IconButton,
  Input,
  Switch,
  Tab,
  TabList,
  Tabs,
  Textarea,
  Tooltip,
  Typography,
} from "@mui/joy";
import { Box, DialogActions, DialogContent } from "@mui/material";
import { Editor } from "@tinymce/tinymce-react";
import DialogClose from "atoms/DialogClose/DialogClose";

import { Booklet_Entity } from "entities/booklet";
import { useAskQuestion } from "features/context/AskQuestion/AskQuestion";
import { useEffect, useState } from "react";
import { renderToString } from "react-dom/server";
import { useDispatch, useSelector } from "react-redux";
import {
  formOpenSelector,
  selectedBookletIDSelector,
  selectedMusicianIDSelector,
  selectedPieceIDSelector,
  selectedProjectIDSelector,
  selectedTagIDSelector,
  selectedVenueIDSelector,
  setFormOpen,
  setSelectedBookletID,
} from "reducers/rhapsody";
import {
  layoutInternalSelector,
  layoutUtilsSelector,
} from "reducers/v2/missionControl";
import {
  useCreateBookletMutation,
  useDeleteBookletMutation,
  useUpdateBookletMutation,
} from "redux/booklet/bookletEndpoints";
import { useBooklet, useBooklets } from "redux/booklet/bookletHooks";
import { useCurrentCompany } from "redux/company/companyHooks";
import { meSelector } from "selectors/me";
import { Editor as TinyMCEEditor } from "tinymce";
import { ProjectBooklets } from "./ProjectBooklets";
import { bookletHTML } from "booklet/helpers";
import { copyTextToClipboard } from "helpers";
import { useUploadFileMutation } from "redux/unity/unity";

/**
 *
 * @returns {ReactElement} FormBooklet page
 */
export function FormBooklet() {
  const [edit, setEdit] = useState(true);
  const me = useSelector(meSelector);
  const bookletID = useSelector(selectedBookletIDSelector);
  const { booklet } = useBooklet(bookletID);
  const open = useSelector(formOpenSelector("booklet"));
  const [_booklet, setBooklet] = useState<Partial<Booklet_Entity>>({});
  const { bookletsMap } = useBooklets();
  const utils = useSelector(layoutUtilsSelector);
  const internal = useSelector(layoutInternalSelector);
  const [editor, setEditor] = useState<TinyMCEEditor>();
  const [tab, setTab] = useState<number>(0);
  const pieceID = useSelector(selectedPieceIDSelector);
  const venueID = useSelector(selectedVenueIDSelector);
  const musicianID = useSelector(selectedMusicianIDSelector);
  const tagID = useSelector(selectedTagIDSelector);
  const projectID = useSelector(selectedProjectIDSelector);
  const { company } = useCurrentCompany();
  const [uploadFile] = useUploadFileMutation();

  const [createBooklet] = useCreateBookletMutation();
  const [updateBooklet] = useUpdateBookletMutation();
  const [deleteBooklet] = useDeleteBookletMutation();

  const askQuestion = useAskQuestion();
  const dispatch = useDispatch();
  const [logo, setLogo] = useState<string>();

  const convertFileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onloadend = () => resolve(reader.result as string);
    });
  };

  useEffect(() => {
    if (company && company.id) {
      if (company?.logoURL) {
        fetch(company?.logoURL)
          .then((response) => {
            if (!response.ok) {
              throw new Error("Failed to fetch image");
            }
            return response.blob();
          })
          .then((blob) => {
            const file = new File([blob], "downloaded-image.png", {
              type: blob.type,
            });

            // Convert File to Base64
            convertFileToBase64(file).then((e) => {
              console.log(e);
              setLogo(e);
            });
          })
          .catch((error) => {
            console.log(error);
            setLogo("-");
          });
      } else {
        setLogo("-");
      }
    }
  }, [company]);

  useEffect(() => {
    if (booklet?.id) {
      setBooklet(booklet);
    }
  }, [booklet]);

  useEffect(() => {
    if (booklet?.id && editor && bookletsMap) {
      editor.setContent(booklet.content);
      refreshDynamicBooklets();
    }
  }, [booklet, editor, bookletsMap]);

  const onClose = () => {
    dispatch(setFormOpen({ isOpen: false, formID: "booklet" }));
    dispatch(setSelectedBookletID());
  };

  const save = async (close: boolean) => {
    if (bookletID && _booklet) {
      updateBooklet({
        id: bookletID,
        body: {
          ..._booklet,
          content: editor ? editor.getContent() : booklet.content,
        },
      });
    } else if (_booklet) {
      const booklet = await createBooklet({
        ..._booklet,
        author: `${me.firstName} ${me.lastName}`,
        content: editor.getContent(),
        pieceID,
        musicianID,
        venueID,
        projectID:
          !musicianID && !tagID && !venueID && !tagID && !pieceID
            ? projectID
            : undefined,
        tagID:
          !musicianID && !tagID && !venueID && !projectID && !pieceID
            ? tagID
            : undefined,
        snippet: !musicianID && !tagID && !venueID && !projectID && !pieceID,
      }).unwrap();

      if (booklet?.id) {
        dispatch(setSelectedBookletID(booklet?.id));
      }
    }
    if (close) onClose();
  };

  const askDelete = () => {
    askQuestion("Are you sure?", ["Cancel", "Yes"], {
      subtitle: (
        <Typography>
          You are about to delete a Project Piece. Chairs linked to this Project
          Piece will be deleted as well. .
        </Typography>
      ),
    }).then((i) => {
      if (i == 1) confirmDelete();
    });
  };

  const confirmDelete = async () => {
    if (bookletID) deleteBooklet(bookletID);
    onClose();
  };

  const refreshDynamicBooklets = () => {
    const doc = editor.getDoc();
    const elements = doc.querySelectorAll("[class^='readonly-block']");

    elements.forEach((el) => {
      const className = el.className;
      const target = className.split(" ")[1];
      if (target.includes("booklet")) {
        const bookletID = parseInt(target.split("-")[1]);
        const booklet = bookletsMap[bookletID];
        el.innerHTML = booklet?.content;
      } else {
        const split = target.split("-");
        const entity = split[0];
        const gen = split[1];
        const id = parseInt(split[2]);
        if (bookletHTML[entity] && bookletHTML[entity][gen]) {
          el.innerHTML = bookletHTML[entity][gen]({
            utils,
            internal,
            id,
          });
        } else {
          el.innerHTML = `ERROR: Dynamic booklet ${entity}-${gen}-${id} not found`;
        }
      }
    });

    editor.setContent(doc.body.innerHTML);
  };

  const editorElement = (
    <Editor
      apiKey="gmpsjaaxrigthm5exowz11sm02iveg5vrpr9dow1of1ia0nb"
      onInit={(_evt, e) => setEditor(e)}
      init={{
        exportpdf_token_provider: () => {
          return fetch("https://jwt.live.rhapsody.la", {
            // specify your token endpoint
            method: "POST",
            headers: { "Content-Type": "application/json" },
          }).then((response) => response.json());
        },
        tinycomments_author: `${me.firstName} ${me.lastName}`,
        tinycomments_mode: "embedded",
        content_style: `body { font-size: 10pt; } .readonly-block { background: #e3f2fd  }
                            /* Apply page-like styling */
        @media (min-width: 840px) {
          html {
            background: #eceef4;
            min-height: 100%;
            padding: 0.5rem;
          }

          body {
            background-color: #fff;
            box-shadow: 0 0 4px rgba(0, 0, 0, .15);
            box-sizing: border-box;
            margin: 1rem auto 0;
            max-width: 820px;
            min-height: calc(100vh - 1rem);
            padding: 1.5rem 3rem 1.5rem 3rem;
          }
        }
                    `,
        statusbar: false,
        height: "100%",
        width: "100%",
        images_upload_handler: async (blobInfo) => {
          try {
            const file = blobInfo.blob();
            const e = await uploadFile(file).unwrap();
            return `${e}`;
          } catch (error) {
            console.log(error);
          }
        },
        noneditable_class: "readonly-block",
        toolbar_mode: "wrap",
        menubar: "file edit view insert tools table help",
        menu: {
          file: { title: "File", items: "exportpdf exportword" },
          insert: {
            title: "Insert",
            items:
              "image link | charmap | emoticons hr | anchor tableofcontents footnotes | lists checklist",
          },
        },
        plugins: [
          // Core editing features
          "anchor",
          "autolink",
          "charmap",
          "noneditable",
          "codesample",
          "emoticons",
          "image",
          "link",
          "lists",
          "media",
          "searchreplace",
          "table",
          "visualblocks",
          "wordcount",
          "pagebreak",
          // Your account includes a free trial of TinyMCE premium features
          // Try the most popular premium features until Mar 27, 2025:
          // "checklist",
          // "mediaembed",
          // "casechange",
          "formatpainter",
          // "pageembed",
          // "a11ychecker",
          // "tinymcespellchecker",
          // "permanentpen",
          // "powerpaste",
          "advtable",
          // "advcode",
          "editimage",

          // "mentions",
          "tinycomments",
          "tableofcontents",
          "footnotes",
          // "autocorrect",
          "typography",
          "inlinecss",
          "markdown",
          "lists",
          "checklist",
          // "importword",
          // "exportword",
          "exportpdf",
        ],
        pagebreak_separator: `<div style="page-break-after: always;"></div>`,
        pagebreak_split_block: true,
        toolbar:
          "blocks fontfamily fontsize | forecolor backcolor | pagebreak bold italic underline strikethrough | link image table | \n typography | align lineheight | checklist numlist bullist indent outdent | emoticons charmap | removeformat | exportpdf exportword | addcomment showcomments",
        mergetags_list: [
          { value: "First.Name", title: "First Name" },
          { value: "Email", title: "Email" },
        ],
        exportpdf_converter_style:
          ".readonly-block { background: white!important  }",
        exportpdf_converter_options: {
          // header_html: renderToString(
          //   <div style={{ padding: "0.5cm" }}>
          //     <div
          //       style={{
          //         display: "flex",
          //         justifyContent: "space-between",
          //       }}
          //     >
          //       <img src={logo} style={{ height: 40 }} />
          //       <div style={{ textAlign: "right" }}>
          //         <b>{utils?.project?.name}</b>
          //         <br />
          //         {company.name}
          //       </div>
          //     </div>

          //     <Divider />
          //   </div>
          // ), // example
          footer_html:
            '<div style="padding: 8px; text-align: right;"><span class="pageNumber"></span></div>', // example
          margin_top: "0.635cm",
          margin_bottom: "0.635cm",
          margin_left: "1.27cm",
          margin_right: "1.27cm",
          format: "Letter",
        },
      }}
      initialValue=""
    />
  );

  return (
    <DialogClose
      open={open}
      onClose={() => {
        askQuestion(
          "Do you want to save?",
          bookletID
            ? [
                "Cancel",
                `Exit Without ${bookletID ? "Saving" : "Creating"}`,
                `${bookletID ? "Save" : "Create"} and Exit`,
              ]
            : ["Cancel", `Save and Exit`],
          {
            subtitle: (
              <Typography>
                Your changes haven’t been saved. If you close now, you’ll lose
                them. Would you like to save before exiting?
              </Typography>
            ),
          }
        ).then((i) => {
          if (!bookletID) {
            if (i === 1) save(true);
          } else {
            if (i === 1) onClose();
            if (i === 2) save(true);
          }
        });
      }}
      fullScreen
      disableAutoFocus
      disableEnforceFocus
      disableRestoreFocus
      sx={{ margin: "16px" }}
      slotProps={{
        root: {
          style: { borderRadius: "8px", overflow: "hidden" },
        },
      }}
    >
      <DialogContent sx={{ display: "flex", p: 0 }}>
        <Box
          sx={{
            display: "flex",
            gap: 1,
            flexDirection: "column",
            width: 380,
            flexShrink: 0,
            position: "relative",
            p: 1,
            justifyContent: "start",
            alignItems: "start",
          }}
        >
          {projectID && !musicianID && !venueID && !pieceID ? (
            <Tabs
              value={tab}
              size="sm"
              sx={{ width: "100%" }}
              onChange={(e, v) => setTab(v as number)}
            >
              <TabList>
                <Tab sx={{ gap: 1 }}>
                  <i className="fa-solid fa-square-info"></i>
                  General
                </Tab>
                <Tab sx={{ gap: 1 }}>
                  <i className="fa-solid fa-wand-magic-sparkles"></i>
                  Insert
                </Tab>
              </TabList>
            </Tabs>
          ) : (
            []
          )}
          {tab === 0 ? (
            <Box
              sx={{
                width: "100%",
                display: "flex",
                gap: 1,
                flexDirection: "column",
              }}
            >
              {booklet ? (
                <Alert color={booklet.active ? "success" : "danger"}>
                  <Box sx={{ flex: 1 }}>
                    <Box
                      sx={{ display: "flex", justifyContent: "spaceBetween" }}
                    >
                      <Typography sx={{ color: "inherit", width: "100%" }}>
                        <b>
                          {booklet.active
                            ? "Sharing is active"
                            : "Sharing not active"}
                        </b>
                      </Typography>
                      <Switch
                        size="sm"
                        onChange={() => {
                          setBooklet((e) => {
                            updateBooklet({
                              id: booklet.id,
                              body: {
                                ...e,
                                active: !(e.active ?? false),
                              },
                            });
                            return {
                              ...e,
                              active: !(e.active ?? false),
                            };
                          });
                        }}
                        color={_booklet.active ? "success" : "danger"}
                        checked={_booklet.active ?? false}
                      />
                    </Box>

                    <Typography level="body2" sx={{ color: "inherit" }}>
                      {_booklet.active
                        ? "Anyone with the link can see the Booklet."
                        : "Nobody can see the Booklet."}
                    </Typography>
                    <Input
                      readOnly
                      endDecorator={
                        <Tooltip
                          title="Copy to clipboard"
                          arrow
                          size="sm"
                          variant="outlined"
                        >
                          <IconButton
                            color="neutral"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              copyTextToClipboard(booklet.publicUrl());
                            }}
                          >
                            <i className="fa-solid fa-copy"></i>
                          </IconButton>
                        </Tooltip>
                      }
                      size="sm"
                      value={booklet.publicUrl()}
                    />
                  </Box>
                </Alert>
              ) : (
                []
              )}
              <Input
                sx={{ flexGrow: 1 }}
                variant="soft"
                startDecorator="Name:"
                value={_booklet.name}
                onChange={(e) =>
                  setBooklet((b) => ({ ...b, name: e.target.value }))
                }
              />
              <Textarea
                sx={{ flexGrow: 1 }}
                variant="soft"
                startDecorator="Description:"
                value={_booklet.description}
                onChange={(e) =>
                  setBooklet((b) => ({ ...b, description: e.target.value }))
                }
              />
              <Input
                sx={{ flexGrow: 1 }}
                variant="soft"
                startDecorator="Author:"
                value={_booklet.author}
                onChange={(e) =>
                  setBooklet((b) => ({ ...b, author: e.target.value }))
                }
              />
              {/* <Divider>Advanced</Divider>
              <Typography level="body3">For PDF Export:</Typography>
              <Input
                sx={{ flexGrow: 1 }}
                variant="soft"
                startDecorator="Header Title:"
                value={_booklet.headerTitle}
                onChange={(e) =>
                  setBooklet((b) => ({ ...b, headerTitle: e.target.value }))
                }
              />
              <Box>
                <Input
                  sx={{ flexGrow: 1 }}
                  variant="soft"
                  startDecorator="Header Subtitle:"
                  value={_booklet.headerSubtitle}
                  onChange={(e) =>
                    setBooklet((b) => ({
                      ...b,
                      headerSubtitle: e.target.value,
                    }))
                  }
                />
              </Box> */}
              {/* <Button
                color="neutral"
                sx={{ background: "white" }}
                variant="outlined"
                onClick={() => {
                  editor?.setContent(
                    `${renderToString(
                      <div>
                        <h4>{_booklet.headerTitle}</h4>
                        <h4>{_booklet.headerSubtitle}</h4>
                      </div>
                    )}${booklet.content}`
                  );
                }}
              >
                Set Header
              </Button> */}
            </Box>
          ) : (
            []
          )}
          {tab === 1 && projectID && !musicianID && !venueID && !pieceID ? (
            <ProjectBooklets
              onAdd={(c) => {
                editor?.insertContent(c);
              }}
            />
          ) : (
            []
          )}
        </Box>
        <Divider orientation="vertical" />
        {edit ? (
          <Box
            sx={{
              flexGrow: 1,
              display: "flex",
              position: "relative",
            }}
          >
            {logo ? editorElement : []}
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              gap: 1,
              flexDirection: "column",
              flex: 1,
              position: "relative",
            }}
          >
            <Button
              variant="outlined"
              color="neutral"
              sx={{
                position: "absolute",
                top: "1rem",
                right: "1rem",
                zIndex: 999,
                background: "white",
                boxShadow: "2px 2px 4px rgba(0, 0, 0, 0.15)",
              }}
              startDecorator={<i className="fa-solid fa-pen"></i>}
              onClick={() => setEdit(true)}
            >
              Edit
            </Button>
            <Box
              sx={{
                background: "#eceef4",
                padding: "0.5rem",
                flexGrow: 1,
                position: "relative",
                height: 0,
                maxHeight: "auto",
                overflow: "scroll",
                cursor: "pointer",
              }}
            >
              <Box
                sx={{
                  backgroundColor: "#fff",
                  boxShadow: "0 0 4px rgba(0, 0, 0, 0.15)",
                  boxSizing: "border-box",
                  margin: "1rem auto 0",
                  maxWidth: "820px",
                  minHeight: "100%",
                  // minHeight: "calc(100vh - 1rem)",
                  padding: "2rem 6rem 2rem 6rem",
                }}
              >
                <div
                  id="printableDiv"
                  dangerouslySetInnerHTML={{
                    __html: booklet?.content,
                  }}
                ></div>
              </Box>
            </Box>
          </Box>
        )}
      </DialogContent>
      <Divider />
      <DialogActions>
        {bookletID ? (
          <Button variant="soft" onClick={askDelete} color="danger">
            Delete
          </Button>
        ) : (
          []
        )}
        <Box sx={{ flexGrow: 1 }} />
        <Button onClick={onClose} color="neutral" variant="soft">
          Exit Without {bookletID ? "Saving" : "Creating"}
        </Button>
        <Button variant="soft" onClick={() => save(false)}>
          {bookletID ? "Save" : "Create"}
        </Button>
        <Button onClick={() => save(true)}>
          {bookletID ? "Save" : "Create"} and Exit
        </Button>
      </DialogActions>
    </DialogClose>
  );
}

const rhapsodyProductionLogo = `data:image/png;base64,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`;
